import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import { router } from 'expo-router';
import { useCallback, useEffect, useState } from 'react';
import { 
  ActivityIndicator, 
  FlatList, 
  Pressable, 
  StyleSheet, 
  Text, 
  View 
} from 'react-native';

import { Fonts } from '@/constants/theme';
import { fetchAssignmentsForTeacher, type AssignmentListItem } from '@/lib/assignments-admin';
import { useAuth } from '@/providers/auth-provider';

export default function TeacherAssignmentsScreen() {
  const { profile } = useAuth();
  const [assignments, setAssignments] = useState<AssignmentListItem[]>([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    if (!profile?.id) {
      setLoading(false);
      return;
    }
    try {
      const data = await fetchAssignmentsForTeacher(profile.id);
      setAssignments(data);
    } catch (error) {
      console.error('Failed to load assignments:', error);
    } finally {
      setLoading(false);
    }
  }, [profile?.id]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const renderItem = ({ item }: { item: AssignmentListItem }) => (
    <View style={styles.assignmentCard}>
      <View style={styles.cardHeader}>
        <View style={styles.iconContainer}>
          <MaterialIcons name="school" size={24} color="#1B5E20" />
        </View>
        <View style={styles.headerTitle}>
          <Text style={styles.classLabel}>{item.class_label}</Text>
          <Text style={styles.subjectLabel}>{item.subject_label}</Text>
        </View>
      </View>
      
      <View style={styles.cardFooter}>
        <Pressable style={styles.actionBtn}>
          <MaterialIcons name="description" size={18} color="#1B5E20" />
          <Text style={styles.actionText}>VIEW SETS</Text>
        </Pressable>
        <View style={styles.dot} />
        <Pressable 
          style={styles.actionBtn}
          onPress={() => router.push({
            pathname: '/teacher/class-students',
            params: { classId: item.class_id, className: item.class_label }
          })}
        >
          <MaterialIcons name="people" size={18} color="#1B5E20" />
          <Text style={styles.actionText}>STUDENTS</Text>
        </Pressable>
      </View>
    </View>
  );

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>My Classes</Text>
        <Text style={styles.subtitle}>
          Classes and subjects assigned to your profile by the administrator.
        </Text>
      </View>

      {loading ? (
        <View style={styles.center}>
          <ActivityIndicator color="#1B5E20" size="large" />
        </View>
      ) : assignments.length === 0 ? (
        <View style={styles.emptyContainer}>
          <MaterialIcons name="assignment-late" size={64} color="#C8E6C9" />
          <Text style={styles.emptyTitle}>No Assignments</Text>
          <Text style={styles.emptyText}>You have not been assigned to any classes yet.</Text>
        </View>
      ) : (
        <FlatList
          data={assignments}
          renderItem={renderItem}
          keyExtractor={(item) => item.id}
          contentContainerStyle={styles.listContent}
          showsVerticalScrollIndicator={false}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F5F7F5',
  },
  header: {
    paddingTop: 60,
    paddingHorizontal: 20,
    paddingBottom: 24,
    backgroundColor: 'white',
    borderBottomWidth: 1,
    borderBottomColor: '#EEF2EE',
  },
  title: {
    fontSize: 28,
    fontWeight: '800',
    color: '#1B5E20',
    fontFamily: Fonts.rounded,
    marginBottom: 6,
  },
  subtitle: {
    fontSize: 14,
    color: 'rgba(46, 125, 50, 0.6)',
    lineHeight: 20,
    fontFamily: Fonts.sans,
  },
  center: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  listContent: {
    padding: 20,
    gap: 16,
  },
  assignmentCard: {
    backgroundColor: 'white',
    borderRadius: 24,
    padding: 20,
    borderWidth: 0.5,
    borderColor: '#C8E6C9',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.05,
    shadowRadius: 10,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    marginBottom: 20,
  },
  iconContainer: {
    width: 52,
    height: 52,
    borderRadius: 16,
    backgroundColor: '#E8F5E9',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    flex: 1,
    gap: 4,
  },
  classLabel: {
    fontSize: 18,
    fontWeight: '800',
    color: '#1C1C1E',
    fontFamily: Fonts.sans,
  },
  subjectLabel: {
    fontSize: 14,
    color: '#2E7D32',
    fontWeight: '600',
  },
  cardFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-around',
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#F0F4F0',
  },
  actionBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  actionText: {
    fontSize: 11,
    fontWeight: '800',
    color: '#1B5E20',
    letterSpacing: 0.5,
  },
  dot: {
    width: 4,
    height: 4,
    borderRadius: 2,
    backgroundColor: '#C8E6C9',
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    gap: 16,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: '800',
    color: '#1B5E20',
    fontFamily: Fonts.rounded,
  },
  emptyText: {
    fontSize: 14,
    color: '#5D7162',
    textAlign: 'center',
    lineHeight: 22,
  },
});
